import { format } from 'date-fns';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Appointment,
  LoadedUser,
  ModalData,
  PiercingLocation,
  Section,
  SectionRow,
} from '../../interfaces';
import {
  formatDollarAmount,
  getBooleanText,
  getCleanedPhone,
  getCustomerNameFromAppointment,
  getEmployeeNameFromAppointment,
  isNil,
} from '../global/helper';
import WaiverIcon from '../../components/icons/WaiverIcon';
import { isCardPaymentMethod } from '../../helpers/payments';
import Badge from '../../components/global/Badge';
import {
  createReferralSourceSection,
  isAppointmentDepositPaid,
} from '../../helpers/appointments';
import { getBusinessCurrency } from '../../helpers/businesses';
import { getUserNameFromBusiness } from '../../helpers/global';
import { getWaiverRequestStatusColor } from '../../helpers/waivers';
import { BusinessWaiverOptions } from '../../types/waivers';

export function getClientSection(
  appointment: Appointment,
  button?: {
    text: string;
    action: (customerId: string) => void;
  },
): Section {
  const customerName = getCustomerNameFromAppointment(appointment);
  const finalPhoneNumber = getCleanedPhone(appointment.customer.phoneNumber);
  let customerDob = '';
  if (appointment.customer.dob) {
    customerDob = appointment.customer.dob?.split('T')[0];
  }

  const firstTattoo = getBooleanText(appointment.isFirstTattoo);
  const newCustomer = getBooleanText(appointment.isNewCustomer);

  const client: Section = {
    name: 'Client',
    rows: [
      {
        type: 'TEXT',
        label: 'Name',
        value: [customerName || ''],
      },
      {
        type: 'TEXT',
        label: 'Booking Location',
        value: [appointment.tripCity || ''],
      },
      {
        type: 'TEXT',
        label: 'Email',
        value: [appointment.customer.email || ''],
      },
      {
        type: 'PHONE',
        label: 'Phone',
        value: [finalPhoneNumber || ''],
      },
      {
        type: 'TEXT',
        label: 'Birthday',
        value: [
          customerDob
            ? format(new Date(`${customerDob}T00:00:00`), 'MM/dd/yyyy')
            : '',
        ],
      },
      {
        type: 'TEXT',
        label: 'IG Handle',
        value: [appointment.customer.igHandle || ''],
      },
      {
        type: 'TEXT',
        label: 'Skin Tone',
        value: [appointment.skinTone || ''],
      },
      {
        type: 'TEXT',
        label: 'First Tattoo',
        value: [firstTattoo || ''],
      },
      {
        type: 'TEXT',
        label: 'New Customer',
        value: [newCustomer || ''],
      },
      {
        type: 'TEXT',
        label: 'Internal Notes',
        value: [appointment.customer.internalNotes || ''],
      },
    ],
    buttonText: button?.text,
    buttonAction: button?.action
      ? () => button.action(appointment.customerId)
      : undefined,
  };
  return client;
}

// Set modal data based on the appointment data
export function createModalData({
  appointment,
  openCheckoutModal,
  sizeOptions,
  navigateToCustomer,
  loadedUser,
  piercingLocations,
  refreshModal,
  waiverButtonAction,
  waivers,
}: {
  appointment: Appointment;
  openCheckoutModal: () => void;
  sizeOptions: { name: string | null; value: string | null }[] | undefined;
  navigateToCustomer: (customerId: string) => void;
  loadedUser: LoadedUser | undefined;
  piercingLocations: PiercingLocation[] | null;
  refreshModal: (appointmentId: string) => void;
  waiverButtonAction?: (appointment: Appointment) => void;
  waivers: BusinessWaiverOptions[] | null;
}): ModalData {
  const isTattoo = appointment.serviceCategory === 'TATTOO';

  const service = loadedUser?.business.services?.find(
    tempService => tempService.serviceId === appointment.serviceId,
  );
  const piercingLocation = piercingLocations?.find(
    location => location.locationId === appointment.piercingLocationId,
  );

  const isConfirmedOrScheduled =
    appointment.status === 'CONFIRMED' || appointment.status === 'SCHEDULED';

  const isDepositPaid = isAppointmentDepositPaid(appointment);

  const status = [];
  if (isConfirmedOrScheduled && appointment.checkoutSessionActive) {
    status.push('Checkout link sent');
  } else if (
    isConfirmedOrScheduled &&
    appointment.appointmentType === 'CONSULTATION'
  ) {
    status.push('Consultation scheduled');
  } else if (isConfirmedOrScheduled && appointment.checkoutSessionCompleted) {
    status.push('Appointment completed');
  } else if (isConfirmedOrScheduled && appointment.wasNoShow) {
    status.push('No show');
  } else if (appointment.status === 'CONFIRMED') {
    status.push('Appointment confirmed');
  } else if (
    appointment.status === 'SCHEDULED' &&
    appointment.depositPrice &&
    !isDepositPaid
  ) {
    status.push('Appointment scheduled (deposit unpaid)');
  } else if (appointment.status === 'SCHEDULED') {
    status.push('Appointment scheduled');
  } else if (appointment.status === 'CANCELED') {
    status.push('Appointment canceled');
  } else {
    status.push(appointment.status);
  }

  const startDate = new Date(appointment.startDateTime!);
  const endDate = new Date(appointment.endDateTime!);

  status.push(format(startDate, 'eeee MMMM d, yyyy'));
  status.push(`${format(startDate, 'p')} - ${format(endDate, 'p')}`);

  const canceledAt = appointment.canceledOn
    ? format(new Date(appointment.canceledOn), 'MM/dd/yyyy, h:mm a')
    : '';

  const employeeName = getEmployeeNameFromAppointment(appointment);

  let size = appointment.tattooSize;
  const sizeName = sizeOptions?.find(option => option.value === size);
  if (sizeName) {
    size = sizeName.name ?? undefined;
  }

  const bookedByCustomer = appointment.bookerUserId === appointment.customerId;
  const bookerName = getUserNameFromBusiness(
    loadedUser,
    bookedByCustomer,
    appointment.bookerUserId,
  );

  const canceledByCustomer =
    appointment.canceledByUserId === appointment.customerId;
  const canceledByName = getUserNameFromBusiness(
    loadedUser,
    canceledByCustomer,
    appointment.canceledByUserId,
  );

  const overviewWaiverButton =
    appointment.waiverRequest && waiverButtonAction
      ? {
          buttonText: (
            <WaiverIcon
              fill={getWaiverRequestStatusColor({
                appointment,
                waivers,
              })}
            />
          ),
          buttonAction: () => waiverButtonAction(appointment),
        }
      : {};

  const overview: Section = {
    name: 'Overview',
    rows: [
      {
        type: 'TEXT',
        label: 'Client',
        value: [getCustomerNameFromAppointment(appointment)],
      },
      {
        type: 'TEXT',
        label: 'Status',
        value: status,
      },
      {
        type: 'TEXT',
        label: isTattoo ? 'Tattoo Artist' : 'Service Provider',
        value: [employeeName],
      },
      !isTattoo && {
        type: 'TEXT',
        label: 'Service',
        value: [service?.serviceName || ''],
      },
      {
        type: 'TEXT',
        label: 'Scheduled by',
        value: [bookerName],
      },
      !!canceledByName && {
        type: 'TEXT',
        label: 'Canceled by',
        value: [canceledByName],
      },
      !!canceledAt && {
        type: 'TEXT',
        label: 'Canceled at',
        value: [canceledAt],
      },
      (appointment.depositPrice ?? 0) > 0 &&
        !(appointment.checkoutCompletedOn && !isDepositPaid) && {
          type: 'NODE',
          label: 'Deposit',
          value: [
            <Box
              key="deposit"
              display="flex"
              alignItems="center"
              gap={1}
              mt={0.5}>
              <Typography variant="body2">
                <Typography
                  component="span"
                  variant="inherit"
                  fontWeight="bold">
                  {appointment.depositPrice
                    ? formatDollarAmount(appointment.depositPrice)
                    : ''}
                </Typography>
                {loadedUser?.business
                  ? ` ${getBusinessCurrency(loadedUser?.business)}`
                  : ''}
              </Typography>
              <Badge
                colorScheme={isDepositPaid ? 'secondary' : 'primary'}
                text={isDepositPaid ? 'Paid' : 'Unpaid'}
                icon={isDepositPaid ? CheckIcon : CloseIcon}
                sx={{
                  width: 'fit-content',
                }}
                tooltip={{
                  placement: 'top',
                  offset: [0, -8],
                  text: isDepositPaid
                    ? `Deposit paid on ${
                        appointment.depositPaidOn || appointment.confirmedOn
                          ? format(
                              new Date(
                                (appointment.depositPaidOn ||
                                  appointment.confirmedOn)!,
                              ),
                              'MM/dd/yyyy',
                            )
                          : 'Unknown'
                      }`
                    : 'Deposit unpaid',
                }}
              />
            </Box>,
          ],
        },
    ].filter(Boolean) as SectionRow[],
    ...overviewWaiverButton,
  };

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const payment: Section = {
    name: 'Payment',
    rows: [],
    values: {
      checkoutAction: openCheckoutModal,
      refreshModal,
      appointment,
    },
  };

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const showTransfers =
    ((appointment.depositPrice ?? 0) > 0 &&
      (appointment.confirmedOn || appointment.depositPaidOn) &&
      isCardPaymentMethod(appointment.depositPaymentMethod)) ||
    ((appointment.price ?? 0) > 0 &&
      appointment.checkoutCompletedOn &&
      isCardPaymentMethod(appointment.paymentMethod));
  const transfers: Section | null = showTransfers
    ? {
        name: 'Transfer Details',
        rows: [],
        values: {
          appointment,
        },
      }
    : null;

  const transactions: Section = {
    name: 'Appointment Transactions',
    rows: [],
    values: {
      appointment,
    },
  };

  const client = getClientSection(appointment, {
    text: 'View',
    action: customerId => navigateToCustomer(customerId),
  });

  const tattooDetails: Section = {
    name: 'Tattoo Details',
    rows: [
      !isNil(appointment.tattooServiceType) && {
        type: 'TEXT',
        label: 'Tattoo Service Type',
        value: [appointment.tattooServiceType || ''],
      },
      {
        type: 'TEXT',
        label: 'Internal Notes',
        value: [appointment.internalNotes || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Location',
        value: [appointment.tattooLocation || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Size',
        value: [size || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Color',
        value: [appointment.tattooColor || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Description',
        value: [appointment.tattooDetails || ''],
      },
      !isNil(appointment.otherNotes) && {
        type: 'TEXT',
        label: 'Other Notes',
        value: [appointment.otherNotes || ''],
      },
      !isNil(appointment.ongoingProject) && {
        type: 'TEXT',
        label: 'Ongoing Project',
        value: [getBooleanText(appointment.ongoingProject) || ''],
      },
      !isNil(appointment.coverUp) && {
        type: 'TEXT',
        label: 'Cover Up',
        value: [getBooleanText(appointment.coverUp) || ''],
      },
      !isNil(appointment.surroundingTattoos) && {
        type: 'TEXT',
        label: 'Surrounding Tattoos',
        value: [getBooleanText(appointment.surroundingTattoos) || ''],
      },
      !isNil(appointment.isEphemeral) && {
        type: 'TEXT',
        label: 'Is Ephemeral?',
        value: [getBooleanText(appointment.isEphemeral) || ''],
      },
    ].filter(Boolean) as SectionRow[],
  };

  const serviceDetails: Section = {
    name: 'Service Details',
    rows: [
      {
        type: 'TEXT',
        label: 'Internal Notes',
        value: [appointment.internalNotes || ''],
      },
      {
        type: 'TEXT',
        label: 'Service',
        value: [service?.serviceName || ''],
      },
      service?.serviceCategory === 'PIERCING' && {
        type: 'TEXT',
        label: 'Piercing Location',
        value: [piercingLocation?.locationCategory || ''],
      },
      service?.serviceCategory === 'PIERCING' && {
        type: 'TEXT',
        label: 'Location Details',
        value: [piercingLocation?.locationType || ''],
      },
      {
        type: 'TEXT',
        label: 'Details',
        value: [appointment.tattooDetails || ''],
      },
    ].filter(Boolean) as SectionRow[],
  };

  const photos: Section = {
    name: 'Photos',
    rows: [
      {
        type: 'IMAGE',
        label: 'Reference Images',
        value: appointment.refImgsId || [''],
      },
      {
        type: 'IMAGE',
        label: 'Area Images',
        value: appointment.areaImgsId || [''],
      },
    ],
  };

  const referralSource = appointment.referralSources?.[0];

  const referralSourceSection = referralSource
    ? createReferralSourceSection(referralSource)
    : null;

  const detailSection = isTattoo ? tattooDetails : serviceDetails;

  const isConsultation = appointment.appointmentType === 'CONSULTATION';

  const sections = [
    overview,
    isConsultation ? null : payment,
    isConsultation ? null : transactions,
    isConsultation ? null : transfers,
    client,
    detailSection,
    photos,
    referralSourceSection,
  ];

  const newModalData = {
    name: !isConsultation ? 'Appointment Detail' : 'Consultation Detail',
    sections: sections.filter(Boolean) as Section[],
  };

  return newModalData;
}
